import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchSupportTickets } from '../api/authApi';
import { StatusBadge } from './StatusBadge';
import {
  supportTicketDisplayId,
  supportTicketLastActivity,
  supportTicketSubject,
} from '../utils/supportTicketIdentity';

function formatDate(value, emptyValue) {
  if (!value) {
    return emptyValue;
  }

  return new Date(value).toLocaleString();
}

export function SupportTicketsTable({ customerId, tenantId, emptyMessage }) {
  const navigate = useNavigate();
  const { t } = useTranslation(['support', 'statuses', 'common']);
  const emptyValue = t('common:empty_value');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (customerId) {
      params.customer_id = customerId;
    }
    if (tenantId) {
      params.tenant_id = tenantId;
    }

    fetchSupportTickets(params)
      .then((response) => setTickets(response.data || []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [customerId, tenantId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (tickets.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage || t('support:no_tickets_found')}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('support:column_ticket')}</TableCell>
            <TableCell>{t('support:column_subject')}</TableCell>
            <TableCell>{t('support:column_status')}</TableCell>
            <TableCell>{t('support:column_priority')}</TableCell>
            <TableCell>{t('support:column_last_activity')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.map((ticket) => (
            <TableRow
              key={ticket.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/support/${ticket.id}`)}
            >
              <TableCell>{supportTicketDisplayId(ticket) || emptyValue}</TableCell>
              <TableCell>{supportTicketSubject(ticket) || emptyValue}</TableCell>
              <TableCell>
                <StatusBadge status={ticket.status} category="support" />
              </TableCell>
              <TableCell>
                <Chip
                  label={t(`statuses:support_priority.${ticket.priority}`, {
                    defaultValue: ticket.priority,
                  })}
                  size="small"
                />
              </TableCell>
              <TableCell>{formatDate(supportTicketLastActivity(ticket), emptyValue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
